import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { safeMediaHref } from "@/lib/safe-url";

export interface LightboxItem {
  url: string | null;
  mediaType?: "image" | "video" | string;
  caption?: string;
}

/** Full-screen media viewer. Pass `items` + `index` for ‹ › navigation. */
export function Lightbox({
  url,
  mediaType,
  caption,
  onClose,
  items,
  index,
  onIndex,
}: {
  url?: string | null;
  mediaType?: string;
  caption?: string;
  onClose: () => void;
  items?: LightboxItem[];
  index?: number;
  onIndex?: (i: number) => void;
}) {
  const cur: LightboxItem = items && index !== undefined ? (items[index] ?? { url: null }) : { url: url ?? null, mediaType, caption };
  const n = items?.length ?? 0;
  const src = safeMediaHref(cur.url);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (items && onIndex && index !== undefined) {
        if (e.key === "ArrowRight") onIndex((index + 1) % n);
        if (e.key === "ArrowLeft") onIndex((index - 1 + n) % n);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, items, index, onIndex, n]);
  return createPortal(
    <div className="fixed inset-0 z-[120] flex animate-fade-in flex-col bg-black/90 backdrop-blur-sm" role="dialog" aria-label="Media viewer">
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-[13px] text-fg/80">
        <span className="truncate">{cur.caption}</span>
        <div className="flex items-center gap-1">
          {src && (
            <a href={src} target="_blank" rel="noreferrer" className="grid size-9 place-items-center rounded-lg hover:bg-white/10" aria-label="Open original">
              <ExternalLink className="size-4" />
            </a>
          )}
          <button type="button" onClick={onClose} className="grid size-9 place-items-center rounded-lg hover:bg-white/10" aria-label="Close">
            <X className="size-5" />
          </button>
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6" onClick={onClose}>
        {src ? (
          cur.mediaType === "video" ? (
            <video src={src} controls autoPlay className="max-h-full max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={src} alt={cur.caption ?? ""} className="max-h-full max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
          )
        ) : (
          <div className="text-muted">No media</div>
        )}
        {items && onIndex && index !== undefined && n > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onIndex((index - 1 + n) % n);
              }}
              className="absolute left-4 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Previous"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onIndex((index + 1) % n);
              }}
              className="absolute right-4 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Next"
            >
              <ChevronRight className="size-5" />
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
